import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageContent,
  Container,
  Button,
  FormActions,
  FormNotice,
  FormSection,
  FormSummary,
  FormSummaryHeading,
  FormSummaryMeta,
  StatusBadge,
} from 'components';
import { formatDateTime } from 'helpers/date';
import {
  loadIpmaMeetingAttendanceCreator,
  saveIpmaMeetingAttendanceCreator,
  removeIpmaMeetingAttendanceCreator,
} from 'store/actionsCreators';
import { describeSubmitter } from 'containers/ma/Meetings/Application/submitter';
import { SummaryList } from 'containers/ma/Meetings/Application/Application.styled';
import StatusHistory from 'containers/ma/Meetings/Application/StatusHistory';
import AttendanceQuestions from './AttendanceQuestions';

/**
 * The attendance form of the logged in IPMA user.
 *
 * There is one per person rather than one per meeting: it asks about every
 * meeting that is open at the time, so a newly scheduled meeting simply appears
 * as another question - and a CoD meeting brings the FRT held with it along as
 * a second one. From there it travels the same road as a Member Association
 * application - submitted, then accepted or rejected, with the reason shown
 * back here.
 */
const AttendanceForm = ({ data, errors, loadAttendance, saveAttendance, clearAttendance }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [answers, setAnswers] = useState({});

  useEffect(() => {
    loadAttendance();

    return () => clearAttendance();
  }, []);

  useEffect(() => {
    if (!data) return;

    setAnswers(data.answers || {});
  }, [data]);

  if (!data) {
    return <PageContent />;
  }

  const { application, questions, dinner } = data;
  const readOnly = !data.editable;
  const submittedBy = describeSubmitter(data.submittedBy);

  const setAnswer = (question, value) =>
    setAnswers((previous) => ({ ...previous, [question]: value }));

  const submitForm = (submit) => saveAttendance({ submit, answers }, history);

  const renderActions = () => {
    if (readOnly) {
      return (
        <FormActions>
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => history.push('/ipma/meetings/attendance')}
          >
            {t('page.ipma.meetings.attendance.back')}
          </Button>
        </FormActions>
      );
    }

    const sent = application && application.status === 'SUBMITTED';

    return (
      <FormActions>
        {!sent && (
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => submitForm(false)}
          >
            {t('page.ma.meetings.application.saveDraft')}
          </Button>
        )}
        <Button type="button" style={{ width: 'auto' }} onClick={() => submitForm(true)}>
          {sent
            ? t('page.ma.meetings.application.saveChanges')
            : t('page.ipma.meetings.attendance.submit')}
        </Button>
        <Button
          type="button"
          secondary
          style={{ width: 'auto' }}
          onClick={() => history.push('/ipma/meetings/attendance')}
        >
          {t('global.cancelButton')}
        </Button>
      </FormActions>
    );
  };

  return (
    <PageContent>
      <Container>
        <form>
          <FormSummary>
            <div>
              <FormSummaryHeading>{t('page.ipma.meetings.attendance.title')}</FormSummaryHeading>
              <FormSummaryMeta>{t('page.ipma.meetings.attendance.description')}</FormSummaryMeta>
            </div>
            {application ? (
              <StatusBadge status={application.status}>{application.statusLabel}</StatusBadge>
            ) : null}
          </FormSummary>

          {data.rejectionReason ? (
            <FormNotice
              type={application && application.status === 'REJECTED' ? 'error' : 'success'}
            >
              {t('page.ma.meetings.application.reason', { reason: data.rejectionReason })}
            </FormNotice>
          ) : null}

          {/* Nothing is open, so there is nothing to say yes or no to yet. */}
          {!data.open ? (
            <FormNotice type="warning">{t('page.ipma.meetings.attendance.noneOpen')}</FormNotice>
          ) : null}
          {data.open && readOnly && application ? (
            <FormNotice type="warning">{t('page.ipma.meetings.attendance.closed')}</FormNotice>
          ) : null}

          {submittedBy ? (
            <FormSection title={t('page.ipma.meetings.application.applicantSection')}>
              <SummaryList>
                <dt>{t('page.ma.meetings.application.submittedBy')}</dt>
                <dd>{submittedBy}</dd>
                <dt>{t('page.ipma.meetings.application.submittedAt')}</dt>
                <dd>{formatDateTime(application.submittedAt) || '-'}</dd>
              </SummaryList>
            </FormSection>
          ) : null}

          <AttendanceQuestions
            questions={questions}
            dinner={dinner}
            answers={answers}
            errors={errors}
            disabled={readOnly}
            onAnswer={setAnswer}
          />

          {renderActions()}

          {application && <StatusHistory items={data.statusHistory} />}
        </form>
      </Container>
    </PageContent>
  );
};

AttendanceForm.propTypes = {
  data: PropTypes.shape({
    questions: PropTypes.arrayOf(PropTypes.shape({})),
    dinner: PropTypes.bool,
    answers: PropTypes.shape({}),
    application: PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      statusLabel: PropTypes.string,
      submittedAt: PropTypes.string,
    }),
    editable: PropTypes.bool,
    open: PropTypes.bool,
    rejectionReason: PropTypes.string,
    statusHistory: PropTypes.arrayOf(PropTypes.shape({})),
    submittedBy: PropTypes.shape({
      name: PropTypes.string,
      organization: PropTypes.string,
      role: PropTypes.string,
    }),
  }),
  errors: PropTypes.objectOf(PropTypes.string),
  loadAttendance: PropTypes.func.isRequired,
  saveAttendance: PropTypes.func.isRequired,
  clearAttendance: PropTypes.func.isRequired,
};

AttendanceForm.defaultProps = {
  data: null,
  errors: {},
};

const mapStateToProps = ({ ipma }) => ({
  data: ipma.meetings.attendanceForm.data,
  errors: ipma.meetings.attendanceForm.errors,
});

const mapDispatchToProps = (dispatch) => ({
  loadAttendance: () => dispatch(loadIpmaMeetingAttendanceCreator()),
  saveAttendance: (payload, history) =>
    dispatch(saveIpmaMeetingAttendanceCreator(payload, history)),
  clearAttendance: () => dispatch(removeIpmaMeetingAttendanceCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AttendanceForm);
